import React from 'react';
import { MousePointerClick, UserPlus, Stethoscope, Repeat, ArrowDown, TrendingDown, HelpCircle, Info } from 'lucide-react';
import type { PartnerAnalyticsFunnel, FunnelStage } from '../../types/admin-partner';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface PartnerFunnelChartProps {
  funnel?: PartnerAnalyticsFunnel;
  isLoading?: boolean;
}

const STAGE_TOOLTIPS: Record<string, string> = {
  clicks: 'Total de acessos e cliques nos links de indicação dos parceiros (ex: /p/CODIGO). Cada clique grava um cookie de rastreamento de 30 dias no navegador do paciente.',
  signups: 'Quantidade de pessoas que clicaram no link e completaram o cadastro na Medikei, vinculando-se automaticamente ao parceiro que indicou.',
  first_appointment: 'Pacientes indicados que realizaram e pagaram sua primeira consulta médica na plataforma. Gera a comissão principal de 12% para o parceiro.',
  recurrence: 'Consultas subsequentes realizadas pelo mesmo paciente nos 6 meses seguintes. Remunera o parceiro com 5% de comissão recorrente sobre cada atendimento.',
};

const STAGE_ICONS: Record<string, React.FC<{ className?: string }>> = {
  clicks: MousePointerClick,
  signups: UserPlus,
  first_appointment: Stethoscope,
  recurrence: Repeat,
};

const STAGE_GRADIENTS: Record<string, { bar: string; text: string; bg: string; badge: string }> = {
  clicks: {
    bar: 'bg-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  },
  signups: {
    bar: 'bg-indigo-500',
    text: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-950/30',
    badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300',
  },
  first_appointment: {
    bar: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300',
  },
  recurrence: {
    bar: 'bg-purple-500',
    text: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-50 dark:bg-purple-950/30',
    badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
  },
};

export const PartnerFunnelChart: React.FC<PartnerFunnelChartProps> = ({ funnel, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 space-y-6 animate-pulse">
        <div className="h-6 w-48 bg-muted rounded"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-muted/60 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const stages: FunnelStage[] = funnel?.stages || [
    {
      stage: 1,
      key: 'clicks',
      name: 'Cliques',
      description: 'Tráfego gerado via links e cupons de afiliados',
      count: 0,
      conversionRateFromPrevious: 100,
      conversionRateOverall: 100,
      dropoffRate: 0,
    },
    {
      stage: 2,
      key: 'signups',
      name: 'Cadastros',
      description: 'Pacientes registrados com atribuição ativa',
      count: 0,
      conversionRateFromPrevious: 0,
      conversionRateOverall: 0,
      dropoffRate: 0,
    },
    {
      stage: 3,
      key: 'first_appointment',
      name: '1ª Consulta Paga',
      description: 'Primeira conversão monetizada com comissão gerada',
      count: 0,
      conversionRateFromPrevious: 0,
      conversionRateOverall: 0,
      dropoffRate: 0,
    },
    {
      stage: 4,
      key: 'recurrence',
      name: 'Recorrência 6 Meses',
      description: 'Consultas subsequentes dentro da janela de atribuição',
      count: 0,
      conversionRateFromPrevious: 0,
      conversionRateOverall: 0,
      dropoffRate: 0,
    },
  ];

  const maxCount = Math.max(...stages.map((s) => s.count), 1);
  const firstCount = stages[0]?.count ?? 0;
  const lastCount = stages[3]?.count ?? 0;
  const overallRate = funnel?.summary?.overallConversionRate ?? (
    firstCount > 0 ? Number(((lastCount / firstCount) * 100).toFixed(2)) : 0
  );

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-xs flex flex-col justify-between">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
          <div>
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              Funil de Conversão de Afiliados
              <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                4 Etapas
              </span>
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Jornada de aquisição do paciente desde o clique inicial até a retenção semestral
            </p>
          </div>

          <div className="flex items-center gap-3 bg-muted/50 p-2.5 rounded-lg border border-border/50">
            <div className="text-right">
              <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                Conversão Ponta a Ponta
              </span>
              <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                {overallRate}%
              </span>
            </div>
            <div className="h-8 w-px bg-border"></div>
            <div className="text-right">
              <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                Total Fundo do Funil
              </span>
              <span className="text-base font-bold text-foreground">
                {stages[3]?.count ?? 0}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {stages.map((st, index) => {
            const Icon = STAGE_ICONS[st.key] || MousePointerClick;
            const styleConfig = STAGE_GRADIENTS[st.key] || {
              bar: 'bg-blue-500',
              text: 'text-blue-600 dark:text-blue-400',
              bg: 'bg-blue-50 dark:bg-blue-950/30',
              badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
            };
            const barWidthPercent = Math.max(8, Math.round((st.count / maxCount) * 100));
            const isLast = index === stages.length - 1;
            const nextStage = stages[index + 1];

            return (
              <div key={st.key} className="relative">
                <div className={`p-4 rounded-xl border border-border/80 ${styleConfig.bg} transition-all hover:border-border hover:shadow-xs`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2.5">
                      <div className={`p-2 rounded-lg bg-card border border-border shadow-xs ${styleConfig.text}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-muted-foreground">
                            Etapa {st.stage}:
                          </span>
                          <span className="text-sm font-bold text-foreground">
                            {st.name}
                          </span>
                          {STAGE_TOOLTIPS[st.key] && (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-muted-foreground/60 hover:text-primary transition-colors p-0.5 rounded-full hover:bg-muted cursor-pointer shrink-0"
                                    aria-label={`Informações sobre ${st.name}`}
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  align="start"
                                  className="max-w-[280px] p-2.5 text-xs bg-slate-900 text-slate-100 dark:bg-slate-100 dark:text-slate-900 rounded-xl shadow-xl border border-white/10 dark:border-slate-800 leading-relaxed font-normal"
                                >
                                  {STAGE_TOOLTIPS[st.key]}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground hidden sm:block">
                          {st.description}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 self-end sm:self-auto">
                      <div className="text-right">
                        <span className="text-lg font-bold text-foreground">
                          {st.count.toLocaleString('pt-BR')}
                        </span>
                        <span className="text-xs text-muted-foreground block sm:hidden">
                          {st.conversionRateOverall}% do total
                        </span>
                      </div>

                      <div className="hidden sm:block text-right min-w-[70px]">
                        <span className="text-xs font-medium text-muted-foreground block">
                          {st.stage === 1 ? '100%' : `${st.conversionRateFromPrevious}%`}
                        </span>
                        <span className="text-[10px] text-muted-foreground/80">
                          {st.stage === 1 ? 'Base' : 'vs etapa anterior'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Barra de progresso visual estilizada */}
                  <div className="h-2.5 w-full bg-card/80 rounded-full overflow-hidden border border-border/50">
                    <div
                      className={`h-full ${styleConfig.bar} rounded-full transition-all duration-500`}
                      style={{ width: `${barWidthPercent}%` }}
                    />
                  </div>
                </div>

                {/* Conector e Indicador de Drop-off entre etapas */}
                {!isLast && nextStage && (
                  <div className="flex items-center justify-center my-1 relative py-0.5">
                    <div className="flex items-center gap-2 bg-card px-2.5 py-0.5 rounded-full border border-border shadow-xs text-[11px] font-medium z-10">
                      <ArrowDown className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Passagem:{' '}
                        <strong className="text-foreground">
                          {nextStage.conversionRateFromPrevious}%
                        </strong>
                      </span>
                      {nextStage.dropoffRate > 0 && (
                        <span className="text-rose-600 dark:text-rose-400 flex items-center gap-0.5 ml-1">
                          <TrendingDown className="h-3 w-3" />
                          -{nextStage.dropoffRate}% perda
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/70" />
          Atribuição calculada via cookie 90 dias com comissão recorrente por 6 meses
        </span>
        <span className="font-medium text-foreground">
          Top-to-Bottom: {overallRate}%
        </span>
      </div>
    </div>
  );
};
